/**
 * Project Overview sub-page — details/members/publications for the project
 * selected in the context bar.
 */

import { Flex, Stack, useTheme } from '@fti/design-system';
import { useUser } from '../../../auth/hooks';
import {
  useProjectPublications,
  useProjectMembers,
  useAddMember,
  useRemoveMember,
} from '../../../api/media-sim/projects';
import { StatusBadge } from '../../../components/media-sim/shared/StatusBadge';
import { ProjectOverview } from '../../../components/media-sim/projects/ProjectOverview';
import { ProjectScopeGate } from './ProjectScopeGate';
import { useMediaSimUser } from '../../../components/media-sim/layout/MediaSimUserContext';
import type { Project } from '../../../types/media-sim';

function OverviewContent({ project }: { project: Project }) {
  const { theme } = useTheme();
  const user = useUser();
  const { isAdmin } = useMediaSimUser();
  const isCreator = project.created_by === user?.sub;

  const { data: publications, refetch: refetchPublications } = useProjectPublications(project.id);
  const { data: members, refetch: refetchMembers } = useProjectMembers(project.id);
  const { addMember } = useAddMember();
  const { removeMember } = useRemoveMember();

  return (
    <Stack gap="md">
      <Flex align="center" gap="sm">
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
          {project.project_name}
        </h1>
        <StatusBadge status={project.status} size="md" />
      </Flex>

      <ProjectOverview
        project={project}
        publications={publications || []}
        members={members || []}
        isAdmin={isAdmin}
        isCreator={isCreator}
        onAddMember={async (userId) => {
          try { await addMember(project.id, userId); refetchMembers(); } catch { /* handled by error state */ }
        }}
        onRemoveMember={async (memberId) => {
          try { await removeMember(project.id, memberId); refetchMembers(); } catch { /* handled by error state */ }
        }}
        onPublicationsRefreshed={refetchPublications}
      />
    </Stack>
  );
}

export function ProjectOverviewPage() {
  return <ProjectScopeGate>{(project) => <OverviewContent project={project} />}</ProjectScopeGate>;
}
